/**
 * Staff-Edit einer Firma "im Namen" (Slice 2 view, OPS-D3).
 *
 * Voller Client-Edit inkl. der useFieldArray-Steps (Geschäftsführung,
 * Zeichnungsberechtigte, Vor-Ort-Kontakte) — Wiederverwendung der Onboarding-
 * Steps (ClientLegalEntityStep / ClientPeopleBillingStep / ClientOperationalStep).
 * Wie StaffWorkerEditForm: alle Steps gestapelt + ein "Speichern", in jedem
 * Status editierbar, Save = clients.staffUpdate (auditiert, payload.onBehalf).
 */
import {
  STAFF_CLEARABLE_CLIENT_FIELDS,
  upsertClientSchema,
  type UpsertClientInput,
} from '@hirebotiq-ops/core';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { trpc } from '../../lib/trpc.js';
import { Button } from '../ui/button.js';
import { Field } from '../ui/field.js';
import { Textarea } from '../ui/textarea.js';
import {
  ClientLegalEntityStep,
  ClientOperationalStep,
  ClientPeopleBillingStep,
} from './ClientOnboardingForm.js';

// Skalare Leerwerte (''), undefined und null raus — Arrays/Booleans/Zahlen
// bleiben, damit Staff Felder leeren + Listen (Geschäftsführer etc.) pflegen kann.
const stripScalarsEmpty = (input: Record<string, unknown>): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    if (v === '' || v === undefined || v === null || (typeof v === 'number' && Number.isNaN(v))) {
      continue;
    }
    out[k] = v;
  }
  return out;
};

const isEmptyScalar = (value: unknown): boolean =>
  value === '' ||
  value === undefined ||
  value === null ||
  (typeof value === 'number' && Number.isNaN(value));

export function StaffClientEditForm({ clientId }: { clientId: string }) {
  const utils = trpc.useUtils();
  const q = trpc.clients.getById.useQuery({ clientId });
  const update = trpc.clients.staffUpdate.useMutation({
    onSuccess: () => {
      toast.success('Firmen-Daten gespeichert (im Namen).');
      utils.clients.getById.invalidate({ clientId });
      utils.clients.list.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  const form = useForm<UpsertClientInput>({
    resolver: zodResolver(upsertClientSchema),
    defaultValues: {
      managingDirectors: [],
      authorizedSignatories: [],
      onsiteContacts: [],
    },
    mode: 'onBlur',
  });

  const directors = useFieldArray({ control: form.control, name: 'managingDirectors' });
  const signatories = useFieldArray({ control: form.control, name: 'authorizedSignatories' });
  const onsite = useFieldArray({ control: form.control, name: 'onsiteContacts' });
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!q.data) return;
    const c = q.data;
    form.reset({
      companyName: c.companyName ?? undefined,
      legalForm: c.legalForm ?? undefined,
      registeredAddressStreet: c.registeredAddressStreet ?? undefined,
      registeredAddressZip: c.registeredAddressZip ?? undefined,
      registeredAddressCity: c.registeredAddressCity ?? undefined,
      hrbNumber: c.hrbNumber ?? undefined,
      districtCourt: c.districtCourt ?? undefined,
      vatId: c.vatId ?? undefined,
      managingDirectors: (c.managingDirectors as { name: string }[] | null) ?? [],
      authorizedSignatories:
        (c.authorizedSignatories as UpsertClientInput['authorizedSignatories']) ?? [],
      billingEmail: c.billingEmail ?? undefined,
      billingAddressDifferent: c.billingAddressDifferent ?? false,
      billingAddressStreet: c.billingAddressStreet ?? undefined,
      billingAddressZip: c.billingAddressZip ?? undefined,
      billingAddressCity: c.billingAddressCity ?? undefined,
      requirePoNumber: c.requirePoNumber ?? false,
      requireCostCenter: c.requireCostCenter ?? false,
      paymentTermsDays: c.paymentTermsDays ?? undefined,
      earlyPaymentDiscount: c.earlyPaymentDiscount ?? undefined,
      timeTrackingMethod: c.timeTrackingMethod ?? undefined,
      onsiteContacts: (c.onsiteContacts as UpsertClientInput['onsiteContacts']) ?? [],
    });
    setNotes(c.notes ?? '');
  }, [q.data, form]);

  const onSave = async () => {
    if (!(await form.trigger())) {
      toast.error('Bitte die markierten Felder prüfen.');
      return;
    }
    const formValues = form.getValues();
    const allValues: Record<string, unknown> = { ...formValues, notes };
    const values = stripScalarsEmpty(allValues);
    const clearFields = STAFF_CLEARABLE_CLIENT_FIELDS.filter(
      (field) => !isEmptyScalar(q.data?.[field]) && isEmptyScalar(allValues[field]),
    );
    if (!formValues.billingAddressDifferent) {
      for (const field of [
        'billingAddressStreet',
        'billingAddressZip',
        'billingAddressCity',
      ] as const) {
        if (!clearFields.includes(field)) clearFields.push(field);
        delete values[field];
      }
    }
    try {
      await update.mutateAsync({ clientId, ...values, clearFields });
    } catch {
      /* Fehler via onError getoastet */
    }
  };

  if (q.isLoading) {
    return <p className="text-sm text-[var(--muted-foreground)]">Lade Formular …</p>;
  }

  return (
    <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
      <section className="space-y-3">
        <h3 className="text-sm font-semibold">Firma (rechtliche Stammdaten)</h3>
        <ClientLegalEntityStep form={form} />
      </section>
      <section className="space-y-3">
        <h3 className="text-sm font-semibold">Personen & Billing</h3>
        <ClientPeopleBillingStep form={form} directors={directors} signatories={signatories} />
      </section>
      <section className="space-y-3">
        <h3 className="text-sm font-semibold">Einsatzbetrieb</h3>
        <ClientOperationalStep form={form} onsite={onsite} />
      </section>
      <section className="space-y-3">
        <h3 className="text-sm font-semibold">Interne Angaben</h3>
        <Field label="Interne Notizen" htmlFor="clientNotes">
          <Textarea
            id="clientNotes"
            rows={4}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </Field>
      </section>
      <div className="flex justify-end">
        <Button onClick={onSave} disabled={update.isPending}>
          {update.isPending ? 'Speichere …' : 'Speichern (im Namen)'}
        </Button>
      </div>
    </form>
  );
}
